import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Loader2,
  Search,
  Play,
  Square,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Eye,
  ListRestart,
  RotateCcw,
  Clock,
  XCircle,
  TrendingUp,
  Pause,
  Plus,
  RefreshCw,
  Trash2,
  Flag,
  Zap,
} from "lucide-react";
import {
  SqlSyncEntity,
  SqlSyncLiveProgress,
  SqlSyncLastScanInfo,
  SqlSyncStatus,
} from "@/lib/api";

// ============================================================================
// Types
// ============================================================================

export type SyncEnvironment = "Staging" | "Production";

export type PipelineStatus =
  | "NeedsScan"
  | "ReadyToSync"
  | "Syncing"
  | "Paused"
  | "HasErrors"
  | "UpToDate"
  | "Disabled";

export interface EntityMetadataPreview {
  previewTotalChanges: number | null;
  previewNewRecords: number | null;
  previewUpdatedRecords: number | null;
  previewDeletedRecords: number | null;
  previewScannedAt: string | null;
  largeChangeThreshold: number;
}

export interface EntityData {
  entity: SqlSyncEntity;
  sourceCount: number | null;
  lastScan: SqlSyncLastScanInfo | null;
  progress: SqlSyncLiveProgress | null;
  metadata?: EntityMetadataPreview;
  // Field mapping info
  hasFieldMappings?: boolean;
  fieldMappingCount?: number;
  fieldSyncPendingCount?: number;
}

export interface SummaryStats {
  activeJobs: number;
  pausedJobs: number;
  totalPending: number;
  totalSynced: number;
  totalFailed: number;
}

export interface EntityTableProps {
  entities: EntityData[];
  recentHistory: SqlSyncStatus[];
  maxRecords: number;
  canExecute?: boolean;
  executeTooltip?: string;
  onScan: (entityType: string) => void;
  onStart: (entityType: string) => void;
  onStop: (entityType: string) => void;
  onResume: (entityType: string) => void;
  onResetAndRescan: (entityType: string) => void;
  onRetryFailed: (entityType: string) => void;
  onViewErrors: (entityType: string) => void;
  onViewPendingData: (entityType: string) => void;
  // Field sync handlers
  onFlagAllFieldSync?: (entityType: string) => void;
  onFieldSync?: (entityType: string) => void;
  scanningEntities: Set<string>;
  startingEntities: Set<string>;
  stoppingEntities: Set<string>;
  resumingEntities: Set<string>;
  resettingEntities: Set<string>;
  retryingFailedEntities: Set<string>;
  // Field sync state
  flaggingFieldSyncEntities?: Set<string>;
  fieldSyncingEntities?: Set<string>;
  summaryStats?: SummaryStats;
  onViewAllErrors?: () => void;
}

// ============================================================================
// Helper Functions
// ============================================================================

const formatNumber = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return "-";
  return num.toLocaleString();
};

const formatRelativeTime = (dateString: string | null | undefined): string => {
  if (!dateString) return "Never";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const getPipelineStatus = (
  entity: SqlSyncEntity,
  progress: SqlSyncLiveProgress | null,
  lastScan: SqlSyncLastScanInfo | null
): PipelineStatus => {
  if (!entity.enabled) return "Disabled";
  if (progress?.status === "Active") return "Syncing";
  if (progress?.status === "Paused") return "Paused";
  if ((progress?.failed || 0) > 0) return "HasErrors";
  if ((progress?.pending || 0) > 0) return "ReadyToSync";
  if (!lastScan) return "NeedsScan";
  return "UpToDate";
};

const getStatusIndicator = (status: PipelineStatus) => {
  switch (status) {
    case "Syncing":
      return <Loader2 className="w-4 h-4 animate-spin text-blue-600" />;
    case "Paused":
      return <Pause className="w-4 h-4 text-amber-600" />;
    case "HasErrors":
      return <AlertCircle className="w-4 h-4 text-red-600" />;
    case "ReadyToSync":
      return <div className="w-3 h-3 rounded-full bg-amber-500" />;
    case "UpToDate":
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    case "NeedsScan":
      return <div className="w-3 h-3 rounded-full bg-gray-400" />;
    case "Disabled":
      return <div className="w-3 h-3 rounded-full bg-gray-300" />;
    default:
      return null;
  }
};

const getStatusLabel = (status: PipelineStatus): string => {
  switch (status) {
    case "Syncing": return "Syncing";
    case "Paused": return "Waiting";
    case "HasErrors": return "Errors";
    case "ReadyToSync": return "Ready";
    case "UpToDate": return "Up to date";
    case "NeedsScan": return "Needs scan";
    case "Disabled": return "Disabled";
    default: return "";
  }
};

const getRowBackground = (status: PipelineStatus): string => {
  switch (status) {
    case "Syncing": return "bg-blue-50/50";
    case "HasErrors": return "bg-red-50/50";
    case "Paused": return "bg-amber-50/50";
    default: return "";
  }
};

// ============================================================================
// Entity Row Component
// ============================================================================

interface EntityRowProps {
  data: EntityData;
  history: SqlSyncStatus[];
  maxRecords: number;
  canExecute?: boolean;
  executeTooltip?: string;
  onScan: () => void;
  onStart: () => void;
  onStop: () => void;
  onResume: () => void;
  onResetAndRescan: () => void;
  onRetryFailed: () => void;
  onViewErrors: () => void;
  onViewPendingData: () => void;
  // Field sync handlers
  onFlagAllFieldSync?: () => void;
  onFieldSync?: () => void;
  isScanning: boolean;
  isStarting: boolean;
  isStopping: boolean;
  isResuming: boolean;
  isResetting: boolean;
  isRetryingFailed: boolean;
  // Field sync state
  isFlaggingFieldSync?: boolean;
  isFieldSyncing?: boolean;
}

function EntityRow({
  data,
  history,
  maxRecords,
  onScan,
  onStart,
  onStop,
  onResume,
  onResetAndRescan,
  onRetryFailed,
  onViewErrors,
  onViewPendingData,
  onFlagAllFieldSync,
  onFieldSync,
  isScanning,
  isStarting,
  isStopping,
  isResuming,
  isResetting,
  isRetryingFailed,
  isFlaggingFieldSync,
  isFieldSyncing,
  canExecute = true,
  executeTooltip,
}: EntityRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { entity, sourceCount, lastScan, progress, metadata, hasFieldMappings, fieldMappingCount, fieldSyncPendingCount } = data;

  const status = getPipelineStatus(entity, progress, lastScan);
  const pending = progress?.pending || 0;
  const dispatched = progress?.dispatched || 0;
  const failed = progress?.failed || 0;
  const isDisabled = status === "Disabled";
  const isBusy = isScanning || isStarting || isStopping || isResuming || isResetting || isRetryingFailed;
  const permissionBlocked = !canExecute;

  // Preview change info from metadata
  const previewChanges = metadata?.previewTotalChanges || 0;
  const isLargeChange = previewChanges > (metadata?.largeChangeThreshold || 5000);
  const previewOnlyWarning = previewChanges > 0 && pending === 0;
  const stagingLooksEmpty =
    previewOnlyWarning &&
    (progress?.total || 0) === 0 &&
    (metadata?.previewNewRecords || 0) > 0;

  return (
    <>
      {/* Main Row */}
      <TableRow
        className={`cursor-pointer hover:bg-muted/50 ${getRowBackground(status)}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <TableCell>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </Button>
        </TableCell>
        <TableCell className="font-medium">{entity.entityType}</TableCell>
        <TableCell>
          <div className="flex items-center gap-2">
            {getStatusIndicator(status)}
            <span className="text-sm">{getStatusLabel(status)}</span>
          </div>
        </TableCell>
        <TableCell className="text-right tabular-nums text-muted-foreground">
          {formatNumber(sourceCount)}
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {pending > 0 ? (
            <span className="text-amber-700 font-medium">{formatNumber(pending)}</span>
          ) : (
            <span className="text-muted-foreground">{formatNumber(pending)}</span>
          )}
        </TableCell>
        <TableCell className="text-right tabular-nums">{formatNumber(dispatched)}</TableCell>
        <TableCell className="text-right tabular-nums">
          {failed > 0 ? (
            <button
              className="text-red-600 font-medium hover:underline"
              onClick={(e) => { e.stopPropagation(); onViewErrors(); }}
            >
              {formatNumber(failed)}
            </button>
          ) : (
            <span className="text-muted-foreground">{formatNumber(failed)}</span>
          )}
        </TableCell>
        <TableCell className="text-muted-foreground text-sm">
          {formatRelativeTime(lastScan?.timestamp)}
        </TableCell>
      </TableRow>

      {/* Expanded Content - conditionally rendered */}
      {isExpanded && (
        <TableRow className="bg-blue-50 hover:bg-blue-50">
          <TableCell colSpan={8} className="p-0">
            <div className="px-6 py-4 space-y-4">
              {/* Actions Row */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Run/Stop Button */}
                {status === "Syncing" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={onStop}
                          disabled={isStopping || permissionBlocked}
                          title={permissionBlocked ? executeTooltip : undefined}
                          className="border-red-300 text-red-700 hover:bg-red-50"
                        >
                          {isStopping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4 mr-1" />}
                          Stop
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Stop the running sync after current batch completes</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : status === "Paused" ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={onResume}
                          disabled={isResuming || permissionBlocked}
                          title={permissionBlocked ? executeTooltip : undefined}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {isResuming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                          Run
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Resume sending already-pending SQL sync records to Dataverse</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={onStart}
                          disabled={isStarting || isDisabled || pending === 0 || permissionBlocked}
                          title={permissionBlocked ? executeTooltip : undefined}
                          className="bg-green-600 hover:bg-green-700 disabled:bg-green-600/50"
                        >
                          {isStarting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
                          Run
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Send {pending > 0 ? pending.toLocaleString() : ''} pending SQL sync records to Dataverse. This does not run change detection first.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {/* Incremental Scan Button */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={onScan}
                          disabled={isScanning || isResetting || isRetryingFailed || isDisabled || status === "Syncing" || permissionBlocked}
                          title={permissionBlocked ? executeTooltip : undefined}
                        >
                        {isScanning ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Search className="h-4 w-4 mr-1" />
                        )}
                        Incremental Scan
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Compare the source view to the staging master table and mark only detected changes as pending. This does not send records to Dataverse.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Full Rescan Button */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onResetAndRescan}
                        disabled={isResetting || isScanning || isRetryingFailed || isDisabled || status === "Syncing" || permissionBlocked}
                        title={permissionBlocked ? executeTooltip : undefined}
                        className="border-blue-300 text-blue-700 hover:bg-blue-50"
                      >
                        {isResetting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ListRestart className="h-4 w-4 mr-1" />
                        )}
                        Full Rescan
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Clear and rebuild the staging master table from source from scratch. This resets cached sync state, but does not start the Dataverse sync run.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Retry Failed Button */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onRetryFailed}
                        disabled={isRetryingFailed || isScanning || isResetting || isDisabled || status === "Syncing" || failed === 0 || permissionBlocked}
                        title={permissionBlocked ? executeTooltip : undefined}
                        className="border-orange-300 text-orange-700 hover:bg-orange-50"
                      >
                        {isRetryingFailed ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCcw className="h-4 w-4 mr-1" />
                        )}
                        Retry Failed
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Move {failed > 0 ? failed.toLocaleString() : ''} failed SQL sync records back to pending so Run can retry them.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* View Pending Data */}
	                  <Button
	                    variant="outline"
	                    size="sm"
	                    onClick={onViewPendingData}
                  disabled={isDisabled}
	                  >
	                    <Eye className="h-4 w-4 mr-1" />
	                    View Pending
	                  </Button>

                {/* Field Sync Buttons - only show if entity has field mappings */}
                {hasFieldMappings && onFlagAllFieldSync && onFieldSync && (
                  <>
                    <div className="w-px h-6 bg-border mx-1" /> {/* Divider */}

                    {/* Flag All for Field Sync */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onFlagAllFieldSync(); }}
                            disabled={isFlaggingFieldSync || isFieldSyncing || isDisabled || permissionBlocked}
                            title={permissionBlocked ? executeTooltip : undefined}
                            className="border-purple-300 text-purple-700 hover:bg-purple-50"
                          >
                            {isFlaggingFieldSync ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Flag className="h-4 w-4 mr-1" />
                            )}
                            Flag All
                          </Button>
	                        </TooltipTrigger>
	                        <TooltipContent>
	                          <p>Mark all synced {entity.entityType} records for field sync so mapped HS fields can be backfilled. This does not rerun SQL sync.</p>
	                        </TooltipContent>
	                      </Tooltip>
	                    </TooltipProvider>

                    {/* Field Sync */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onFieldSync(); }}
                            disabled={isFieldSyncing || isFlaggingFieldSync || isDisabled || (fieldSyncPendingCount || 0) === 0 || permissionBlocked}
                            title={permissionBlocked ? executeTooltip : undefined}
                            className="border-purple-300 text-purple-700 hover:bg-purple-50"
                          >
                            {isFieldSyncing ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Zap className="h-4 w-4 mr-1" />
                            )}
                            Field Sync
                            {(fieldSyncPendingCount || 0) > 0 && (
                              <Badge variant="secondary" className="ml-1 bg-purple-100 text-purple-700 text-xs px-1.5">
                                {formatNumber(fieldSyncPendingCount)}
                              </Badge>
                            )}
                          </Button>
	                        </TooltipTrigger>
	                        <TooltipContent>
	                          <p>Apply field mappings to {fieldSyncPendingCount?.toLocaleString() || 0} flagged records, copying source/KUP values into mapped HS fields.</p>
	                        </TooltipContent>
	                      </Tooltip>
	                    </TooltipProvider>
                  </>
                )}

	                {maxRecords > 0 && (
	                  <Badge variant="secondary" className="bg-amber-100 text-amber-700">
	                    Test: {maxRecords} max
	                  </Badge>
	                )}
	              </div>

                  <div className="rounded-lg border bg-muted/30 px-3 py-3 text-sm">
                    <div className="font-medium text-foreground mb-2">What These Actions Do</div>
                    <div className="grid gap-2 text-muted-foreground md:grid-cols-2">
                      <div><span className="font-medium text-foreground">Run:</span> send records already marked as pending to Dataverse.</div>
                      <div><span className="font-medium text-foreground">Incremental Scan:</span> compare source to staging and mark only detected changes as pending.</div>
                      <div><span className="font-medium text-foreground">Full Rescan:</span> rebuild the staging table from source from scratch; use this when cached staging data looks wrong.</div>
                      <div><span className="font-medium text-foreground">Retry Failed:</span> move failed sync records back to pending so they can be retried.</div>
                      <div><span className="font-medium text-foreground">View Pending:</span> inspect the rows currently queued for SQL sync.</div>
                      {hasFieldMappings && (
                        <div><span className="font-medium text-foreground">Flag All / Field Sync:</span> queue and run field-mapping backfills for HS fields after SQL sync has populated the source values.</div>
                      )}
                    </div>
                  </div>

	              {/* Preview Changes (from scheduled metadata refresh) */}
	              {metadata && metadata.previewScannedAt && (
                <div className="border-t pt-4">
                  <div className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Preview: Next Incremental Scan
                    <span className="font-normal text-xs">
                      (scanned {formatRelativeTime(metadata.previewScannedAt)})
                    </span>
                  </div>
                  {previewChanges > 0 ? (
                    <div className={`p-3 rounded-lg border ${isLargeChange ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                      <div className="flex items-center gap-6 text-sm">
                        <div className={`font-medium ${isLargeChange ? 'text-amber-700' : 'text-blue-700'}`}>
                          {formatNumber(previewChanges)} total changes
                          {isLargeChange && (
                            <Badge variant="secondary" className="ml-2 bg-amber-200 text-amber-800">
                              Large Change
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-muted-foreground">
                          {(metadata.previewNewRecords || 0) > 0 && (
                            <span className="flex items-center gap-1">
                              <Plus className="h-3 w-3 text-green-600" />
                              {formatNumber(metadata.previewNewRecords)} new
                            </span>
                          )}
                          {(metadata.previewUpdatedRecords || 0) > 0 && (
                            <span className="flex items-center gap-1">
                              <RefreshCw className="h-3 w-3 text-blue-600" />
                              {formatNumber(metadata.previewUpdatedRecords)} updated
                            </span>
                          )}
                          {(metadata.previewDeletedRecords || 0) > 0 && (
                            <span className="flex items-center gap-1">
                              <Trash2 className="h-3 w-3 text-red-600" />
                              {formatNumber(metadata.previewDeletedRecords)} deleted
                            </span>
                          )}
                        </div>
                      </div>
                      {previewOnlyWarning && (
                        <div className="mt-3 rounded-md border border-amber-200 bg-white/70 px-3 py-2 text-sm text-amber-800">
                          Preview only: these changes are not queued yet, so <span className="font-medium">Run</span> stays disabled until this entity has a non-zero <span className="font-medium">Pending</span> count.
                          {stagingLooksEmpty && (
                            <span> For this entity, that usually means the staging table was reset and needs a successful scan or rebuild before it can be run.</span>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
                      No changes detected - source and master tables are in sync
                    </div>
                  )}
                </div>
              )}

              {/* Recent Run History */}
              <div className="border-t pt-4">
                <div className="text-sm font-medium text-muted-foreground mb-2">Recent Runs</div>
                {history.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">No sync runs yet</div>
                ) : (
                  <div className="space-y-2">
                    {history.slice(0, 5).map((run, index) => (
                      <div
                        key={`${run.jobId}_${index}`}
                        className={`flex items-center justify-between p-2 rounded text-sm ${
                          run.status === "Completed" ? "bg-green-50 border border-green-200" :
                          run.status === "Failed" ? "bg-red-50 border border-red-200" :
                          run.status === "InProgress" ? "bg-blue-50 border border-blue-200" :
                          run.status === "Cancelled" ? "bg-gray-50 border border-gray-200" :
                          "bg-white border border-gray-200"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {run.status === "Completed" && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                          {run.status === "Failed" && <AlertCircle className="w-4 h-4 text-red-600" />}
                          {run.status === "InProgress" && <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
                          {run.status === "Cancelled" && <Pause className="w-4 h-4 text-gray-500" />}
                          {run.status === "Pending" && <div className="w-4 h-4 rounded-full bg-gray-300" />}
                          <span className="font-medium">{run.status}</span>
                          <span className="text-muted-foreground">
                            {formatRelativeTime(run.startedAt)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-muted-foreground">
                          <span>{formatNumber(run.successfulRecords)} synced</span>
                          {run.failedRecords > 0 && (
                            <span className="text-red-600">{formatNumber(run.failedRecords)} failed</span>
                          )}
                          <span className="text-xs">{run.jobId.slice(0, 8)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ============================================================================
// Main Table Component
// ============================================================================

export function EntityTable({
  entities,
  recentHistory,
  maxRecords,
  canExecute = true,
  executeTooltip,
  onScan,
  onStart,
  onStop,
  onResume,
  onResetAndRescan,
  onRetryFailed,
  onViewErrors,
  onViewPendingData,
  onFlagAllFieldSync,
  onFieldSync,
  scanningEntities,
  startingEntities,
  stoppingEntities,
  resumingEntities,
  resettingEntities,
  retryingFailedEntities,
  flaggingFieldSyncEntities,
  fieldSyncingEntities,
  summaryStats,
  onViewAllErrors,
}: EntityTableProps) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table className="table-fixed w-full">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead className="w-[120px]">Entity</TableHead>
              <TableHead className="w-[120px]">Status</TableHead>
              <TableHead className="w-[100px] text-right">Source</TableHead>
              <TableHead className="w-[100px] text-right">Pending</TableHead>
              <TableHead className="w-[100px] text-right">Synced</TableHead>
              <TableHead className="w-[80px] text-right">Failed</TableHead>
              <TableHead className="w-[100px]">Last Scan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  No entities configured
                </TableCell>
              </TableRow>
            ) : (
              entities.map((data) => (
                <EntityRow
                  key={data.entity.entityType}
                  data={data}
                  history={recentHistory.filter(h => h.entityType === data.entity.entityType)}
                  maxRecords={maxRecords}
                  canExecute={canExecute}
                  executeTooltip={executeTooltip}
                  onScan={() => onScan(data.entity.entityType)}
                  onStart={() => onStart(data.entity.entityType)}
                  onStop={() => onStop(data.entity.entityType)}
                  onResume={() => onResume(data.entity.entityType)}
                  onResetAndRescan={() => onResetAndRescan(data.entity.entityType)}
                  onRetryFailed={() => onRetryFailed(data.entity.entityType)}
                  onViewErrors={() => onViewErrors(data.entity.entityType)}
                  onViewPendingData={() => onViewPendingData(data.entity.entityType)}
                  onFlagAllFieldSync={onFlagAllFieldSync ? () => onFlagAllFieldSync(data.entity.entityType) : undefined}
                  onFieldSync={onFieldSync ? () => onFieldSync(data.entity.entityType) : undefined}
                  isScanning={scanningEntities.has(data.entity.entityType)}
                  isStarting={startingEntities.has(data.entity.entityType)}
                  isStopping={stoppingEntities.has(data.entity.entityType)}
                  isResuming={resumingEntities.has(data.entity.entityType)}
                  isResetting={resettingEntities.has(data.entity.entityType)}
                  isRetryingFailed={retryingFailedEntities.has(data.entity.entityType)}
                  isFlaggingFieldSync={flaggingFieldSyncEntities?.has(data.entity.entityType)}
                  isFieldSyncing={fieldSyncingEntities?.has(data.entity.entityType)}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Summary Footer Bar */}
      {summaryStats && (
        <div className="border-t bg-muted/30 px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Status Badges */}
            <div className="flex items-center gap-3">
              {summaryStats.activeJobs > 0 && (
                <Badge variant="secondary" className="bg-blue-100 text-blue-700 gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {summaryStats.activeJobs} Active
                </Badge>
              )}
              {summaryStats.pausedJobs > 0 && (
                <Badge variant="secondary" className="bg-amber-100 text-amber-700 gap-1">
                  <Pause className="h-3 w-3" />
                  {summaryStats.pausedJobs} Waiting
                </Badge>
              )}
              {summaryStats.activeJobs === 0 && summaryStats.pausedJobs === 0 && (
                <span className="text-sm text-muted-foreground">No active jobs</span>
              )}
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-amber-600" />
                <span className="text-muted-foreground">Pending:</span>
                <span className="font-medium text-amber-700">{formatNumber(summaryStats.totalPending)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-muted-foreground">Synced:</span>
                <span className="font-medium text-green-700">{formatNumber(summaryStats.totalSynced)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <XCircle className="h-4 w-4 text-red-600" />
                <span className="text-muted-foreground">Failed:</span>
                {summaryStats.totalFailed > 0 ? (
                  <button
                    onClick={onViewAllErrors}
                    className="font-medium text-red-600 hover:underline"
                  >
                    {formatNumber(summaryStats.totalFailed)}
                  </button>
                ) : (
                  <span className="font-medium text-muted-foreground">{formatNumber(summaryStats.totalFailed)}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EntityTable;
